<script setup>
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { DEV_MODE } from '@/config/dev-mode';

definePage({ meta: { layout: 'blank' } });

const router = useRouter();

onMounted(() => {
  if (!DEV_MODE) router.replace('/main');
});

// ─── Play Store 내부 테스트 ─────────────────────────────
const INTERNAL_TEST_URL = 'https://play.google.com/apps/internaltest/4701656631143250229';

const openInternalTest = () => {
  window.open(INTERNAL_TEST_URL, '_blank', 'noopener');
};

// ─── 선물/쿠폰 ────────────────────────────────────────
const giftCode = ref('');
const openGiftPage = () => {
  const code = giftCode.value.trim();
  if (!code) return;
  window.open(`https://nm-gift.thegam.io/${code}`, '_blank', 'noopener');
};

// ─── 파트너 ─────────────────────────────────────────
const SMARTWEL_TOKEN_API =
  'https://dev.buyis.co.kr/api/partner/v1/dev/test-token?lmmfIdx=test&mbsdId=incoaching';

const smartwelToken = ref('');

// 입력값에서 token 추출: 순수 토큰 문자열 또는 JSON 응답 전체를 붙여넣어도 동작
const extractedToken = computed(() => {
  const raw = smartwelToken.value.trim();
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.token) return String(parsed.token);
  } catch {
    // JSON 아니면 raw 그대로 사용
  }
  return raw;
});

const smartwelHttpsUrl = computed(() =>
  extractedToken.value
    ? `https://d25web.thegam.io/partner/smartwel?token=${extractedToken.value}`
    : '',
);
const smartwelIntentUrl = computed(() =>
  extractedToken.value
    ? `intent://d25web.thegam.io/partner/smartwel?token=${extractedToken.value}#Intent;scheme=https;package=com.twofivedosajuapp;end`
    : '',
);

const openTokenIssuePage = () => {
  window.open(SMARTWEL_TOKEN_API, '_blank', 'noopener');
};
</script>

<template>
  <div v-if="DEV_MODE" class="devmode">
    <div class="devmode__content">
      <!-- 1. Play Store 내부 테스트 -->
      <h2 class="devmode__section-title">Play Store 내부 테스트</h2>
      <p class="devmode__description">
        Android 내부 테스트 트랙 페이지를 새 탭에서 엽니다.
      </p>
      <button class="devmode__btn devmode__btn--primary" @click="openInternalTest">
        내부 테스트 페이지 열기
      </button>

      <div class="devmode__divider" />

      <!-- 2. 선물 / 쿠폰 -->
      <h2 class="devmode__section-title">선물 / 쿠폰</h2>
      <p class="devmode__description">
        코드를 입력하면 <code>nm-gift.thegam.io/{코드}</code> 페이지를 엽니다.
      </p>
      <div class="devmode__input-row">
        <input
          v-model="giftCode"
          class="devmode__input"
          placeholder="선물/쿠폰 코드 입력"
          autocapitalize="off"
          autocorrect="off"
          @keyup.enter="openGiftPage"
        />
      </div>
      <div class="devmode__btn-row">
        <button class="devmode__btn devmode__btn--primary" @click="openGiftPage">
          이동
        </button>
        <button class="devmode__btn devmode__btn--secondary" @click="giftCode = ''">
          초기화
        </button>
      </div>

      <div class="devmode__divider" />

      <!-- 3. 파트너 -->
      <h2 class="devmode__section-title">파트너</h2>
      <p class="devmode__description">
        Universal Link / App Links 동작 검증용. HTTPS 링크는 OS가 앱 또는 브라우저로 자동 분기,
        Intent 링크는 Android에서 앱을 강제 호출합니다.
      </p>

      <p class="devmode__sub-title">복지찬스 테스트 토큰으로 진입</p>
      <p class="devmode__description">
        ① 발급 페이지를 새 탭에서 열어 응답 JSON을 복사 →
        ② 아래 칸에 붙여넣으면 토큰이 추출되어 링크가 자동 생성됩니다.
        (CORS 정책으로 페이지에서 직접 fetch는 불가)
      </p>

      <button class="devmode__btn devmode__btn--primary" @click="openTokenIssuePage">
        토큰 발급 페이지 열기
      </button>

      <div class="devmode__input-row" style="margin-top: 12px">
        <textarea
          v-model="smartwelToken"
          class="devmode__textarea"
          rows="3"
          placeholder='토큰 또는 JSON 응답 전체를 붙여넣으세요&#10;예) eyJhbGciOi... 또는 {"status":"success","token":"..."}'
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
        />
      </div>
      <div class="devmode__btn-row">
        <button
          class="devmode__btn devmode__btn--secondary"
          @click="smartwelToken = ''"
        >
          초기화
        </button>
      </div>

      <div v-if="extractedToken" class="devmode__result">
        <p class="devmode__info-label">추출된 token</p>
        <p class="devmode__token">{{ extractedToken }}</p>

        <p class="devmode__info-label" style="margin-top: 12px">HTTPS 링크</p>
        <a
          class="devmode__link"
          :href="smartwelHttpsUrl"
          target="_blank"
          rel="noopener"
        >
          {{ smartwelHttpsUrl }}
        </a>

        <p class="devmode__info-label" style="margin-top: 12px">Android Intent</p>
        <a class="devmode__link devmode__link--intent" :href="smartwelIntentUrl">
          {{ smartwelIntentUrl }}
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.devmode {
  min-height: 100dvh;
  background: #f9f9f9;
  display: flex;
  flex-direction: column;

  &__content {
    flex: 1;
    padding: 16px;
    padding-bottom: 40px;
  }

  &__info-label {
    margin: 0;
    font-size: 13px;
    color: #888;
    margin-bottom: 4px;
  }

  &__divider {
    height: 1px;
    background: #e0e0e0;
    margin: 20px 0;
  }

  &__section-title {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 700;
    color: #333;
  }

  &__sub-title {
    margin: 24px 0 8px;
    font-size: 14px;
    font-weight: 700;
    color: #444;
  }

  &__description {
    margin: 0 0 16px;
    font-size: 13px;
    color: #666;
    line-height: 1.55;

    code {
      padding: 1px 5px;
      background: rgba(0, 0, 0, 0.06);
      border-radius: 3px;
      font-size: 12px;
    }
  }

  &__input-row {
    margin-bottom: 12px;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 0 14px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 16px;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #3a86f6;
    }

    &::placeholder {
      color: #aaa;
    }
  }

  &__textarea {
    width: 100%;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 16px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    line-height: 1.5;
    box-sizing: border-box;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #3a86f6;
    }

    &::placeholder {
      color: #aaa;
    }
  }

  &__btn-row {
    display: flex;
    flex-direction: row;
    gap: 12px;
    margin-bottom: 4px;
  }

  &__btn {
    flex: 1;
    height: 48px;
    padding: 0 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    cursor: pointer;
    width: 100%;

    &:hover {
      opacity: 0.9;
    }

    &:disabled {
      background: #c8c8c8;
      cursor: not-allowed;
    }

    &--primary {
      background: #3a86f6;
    }

    &--secondary {
      background: #888;
    }
  }

  &__result {
    margin-top: 16px;
    background: #fff;
    border-radius: 10px;
    padding: 16px;
  }

  &__token {
    margin: 0;
    font-size: 12px;
    color: #333;
    word-break: break-all;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }

  &__link {
    display: block;
    padding: 10px 12px;
    background: #f0f6ff;
    border-radius: 6px;
    color: #1565c0;
    font-size: 13px;
    text-decoration: underline;
    word-break: break-all;

    &--intent {
      background: #e8f5e9;
      color: #2e7d32;
    }
  }
}
</style>
